<template>
  <BContainer
    class="p-0 py-2"
    fluid
  >
    <ValidationObserver
      ref="formInformation"
    >
      <form
        role="form"
        novalidate
        @submit.prevent
      >
        <BRow>
          <BCol
            cols="12"
            md="4"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="cluster_id"
              name="cluster"
              rules="required"
            >
              <BFormGroup
                label="Cluster"
                label-for="fCluster"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <VSelect
                  v-model="selected.cluster"
                  input-id="fCluster"
                  placeholder="select cluster here"
                  label="cluster_name"
                  clearable
                  append-to-body
                  :options="(options.clusters)"
                  :loading="(fetching.clusters)"
                  :disabled="(state.busy || fetching.clusters)"
                  :calculate-position="calculateDropPosition"
                  :class="{ 'is-invalid': errors.length > 0 }"
                >
                  <template #option="{ cluster_name }">
                    <div
                      class="text-break text-wrap"
                      v-text="cluster_name"
                    />
                  </template>
                  <template #no-options="">
                    no available clusters
                  </template>
                </VSelect>
                <div
                  v-if="errors.length > 0"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </BCol>

          <BCol
            cols="12"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="lead_name"
              name="lead name"
              rules="max:1000"
            >
              <BFormGroup
                label="Lead Name"
                label-for="fRemarks"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <template #label>
                  <span class="d-flex justify-content-between">
                    Lead Name
                    <span>
                      {{ formInformation.lead_name ? formInformation.lead_name.length : 0 }} / 1000
                    </span>
                  </span>
                </template>
                <BFormTextarea
                  id="fRemarks"
                  v-model="formInformation.lead_name"
                  rows="2"
                  max-rows="4"
                  maxlength="1000"
                  placeholder="enter lead name here"
                  :state="errors.length > 0 ? false : null"
                  :disabled="state.busy"
                />
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </BCol>

          <BCol
            cols="12"
            class="py-2"
          >
            <div class="border-bottom" />
          </BCol>

          <BCol
            cols="12"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="contact_person"
              name="contact person"
              rules="required"
            >
              <BFormGroup
                label="Contact Person"
                label-for="fContactPerson"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormInput
                  id="fContactPerson"
                  v-model="formInformation.contact_person"
                  placeholder="enter contact person here"
                  :state="errors.length > 0 ? false : null"
                  :disabled="state.busy"
                />
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </BCol>

          <BCol
            cols="12"
            md="4"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="mobile_number"
              name="mobile number"
              rules="min:11|max:11|regex:[0]{1}[9]{1}[0-9]{8}"
            >
              <BFormGroup
                label="Mobile Number"
                label-for="fMobileNumber"
                label-class="font-weight-bolder"
                description="Only if lead has network coverage"
                class="mb-2"
              >
                <BFormInput
                  id="fMobileNumber"
                  v-model="formInformation.mobile_number"
                  v-mask="'0##########'"
                  placeholder="enter mobile number here"
                  :state="errors.length > 0 ? false : null"
                  :disabled="state.busy"
                />
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </BCol>

          <BCol
            cols="12"
            md="8"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="email"
              name="email"
              rules="email"
            >
              <BFormGroup
                label="Email"
                label-for="fEmail"
                label-class="font-weight-bolder"
                description="Only if lead has an email."
                class="mb-2"
              >
                <BFormInput
                  id="fEmail"
                  v-model="formInformation.email"
                  placeholder="enter email here"
                  type="email"
                  :state="errors.length > 0 ? false : null"
                  :disabled="state.busy"
                />
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </BCol>

          <BCol
            cols="12"
            md="6"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="address"
              name="address"
              rules="max:1000"
            >
              <BFormGroup
                label="Address"
                label-for="fAddress"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <template #label>
                  <span class="d-flex justify-content-between">
                    Address
                    <span>
                      {{ formInformation.address ? formInformation.address.length : 0 }} / 1000
                    </span>
                  </span>
                </template>
                <BFormTextarea
                  id="fAddress"
                  v-model="formInformation.address"
                  rows="5"
                  max-rows="12"
                  maxlength="1000"
                  placeholder="enter address here"
                  :state="errors.length > 0 ? false : null"
                  :disabled="state.busy"
                  @keydown.enter="preventEnterKey"
                />
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </BCol>

          <BCol
            cols="12"
            md="6"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="remarks"
              name="remarks"
              rules="max:1000"
            >
              <BFormGroup
                label="Other Informations"
                label-for="fRemarks"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <template #label>
                  <span class="d-flex justify-content-between">
                    Other Informations
                    <span>
                      {{ formInformation.remarks ? formInformation.remarks.length : 0 }} / 1000
                    </span>
                  </span>
                </template>
                <BFormTextarea
                  id="fRemarks"
                  v-model="formInformation.remarks"
                  rows="5"
                  max-rows="12"
                  maxlength="1000"
                  placeholder="enter other informations here"
                  :state="errors.length > 0 ? false : null"
                  :disabled="state.busy"
                />
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </BCol>
        </BRow>
      </form>
    </ValidationObserver>
  </BContainer>
</template>
<script>
import { find } from 'lodash'
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'

import { SFSOLead } from '@/services/fso'
import { getClusters } from '@/forage/shared/clusters'

export default {
  name: 'VFSOLeadInformations',

  mixins: [
    MxMisc
  ],

  emits: ['updated'],

  data () {
    return {
      flatPickerConfig: {
        appendTo: document.body,
        altInput: true,
        allowInput: true,
        disableMobile: true,
        dateFormat: 'Y-m-d'
      },
      state: {
        busy: false,
        editing: false
      },
      selected: {
        cluster: '',
        tagging: 1
      },
      fetching: {
        clusters: false,
        gender: false
      },
      options: {
        gender: ['Male', 'Female'],
        yesOrNos: [
          {
            text: 'No',
            value: '0'
          },
          {
            text: 'Yes',
            value: '1'
          }
        ],
        clusters: []
      },
      formInformation: {
        id: '',
        cluster_id: '',
        lead_name: '',
        contact_person: '',
        email: '',
        mobile_number: '',
        address: '',
        remarks: '',
        status: '',
        active: 1
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    isVerified () {
      return this.formInformation.status === 'Verified'
    }
  },

  watch: {
    'selected.cluster' (cluster) {
      this.formInformation.cluster_id = cluster?.id || ''
    }
  },

  methods: {

    isFetchingLocations () {
      return this.fetching.provinces || this.fetching.municipalities || this.fetching.barangays
    },

    setInformation (lead) {
      this.formInformation = Object.assign(this.formInformation, lead)
    },

    getInformation () {
      return this.formInformation
    },

    async showInformation (leadID) {
      return await SFSOLead.show(leadID).then(
        ({ data: { lead } }) => lead
      )
    },

    async createInformation () {
      this.state.editing = false

      this.formInformation = Object.assign(this.formInformation, {
        id: '',
        cluster_id: '',
        lead_name: '',
        contact_person: '',
        email: '',
        mobile_number: '',
        address: '',
        remarks: '',
        active: 1,
        status: ''
      })

      getClusters().then(
        clusters => {
          this.options.clusters = clusters
        }
      )
    },

    async editInformation (lead) {
      this.state.editing = true

      this.formInformation = Object.assign(this.formInformation, {
        id: lead.id,
        cluster_id: '',
        lead_name: lead.lead_name,
        contact_person: lead.contact_person,
        email: lead.email,
        mobile_number: lead.mobile_number,
        address: lead.address,
        remarks: lead.remarks,
        active: lead.active,
        status: ''
      })

      getClusters().then(
        clusters => {
          this.options.clusters = clusters
        }
      ).finally(() => {
        this.selected.cluster = find(this.options.clusters, {
          id: lead.cluster_id
        }) || ''
      })
    },

    async validateInformation () {
      return new Promise((resolve, reject) => {
        this.$refs.formInformation.validate().then(
          isFormValid => {
            if (isFormValid) {
              return resolve(isFormValid)
            }

            return reject(new Error('Invalid Form'))
          }
        )
      })
    },

    async submitInformation () {
      return new Promise(resolve => {
        this.validateInformation().then(
          () => {
            this.swalConfirm({
              html: this.state.editing ? 'Update this Lead?' : 'Register this Lead?',
              confirmButtonText: this.state.editing ? 'Update' : 'Register',
              cancelButtonText: 'Discard',
              preConfirm: async () => {
                this.state.busy = true

                const createOrUpdate = () => {
                  return this.state.editing ? this.putInformation() : this.postInformation()
                }

                return createOrUpdate().catch(
                  ({ message }) => {
                    if (message && message?.errors) {
                      this.$refs.formInformation.setErrors(message?.errors)
                    }
                  }
                ).finally(() => {
                  this.state.busy = false
                })
              }
            }).then(resolve)
          }
        ).catch(() => {
          this.swalInvalid()
        })
      })
    },

    async postInformation () {
      return await SFSOLead.post(this.formInformation).then(
        ({ data }) => data
      )
    },

    async putInformation () {
      return await SFSOLead.put(this.formInformation).then(
        ({ data }) => data
      )
    },

    setErrors (errors) {
      if (errors) {
        this.$refs.formInformation.setErrors(errors)
      }
    }
  }
}
</script>
